// Едно правило в списъка — ЕДИН ред в общата карта: равенството в чипове
// („1 кг Кафе A = 0.7 кг Кафе B", същото изречение като картите в редактора),
// под него — кой се изписва пръв (при 3+ продукта) и коментарът; вдясно
// „Редактирай". Целият ред отваря редакцията (клик или Enter); ↑/↓ между
// редовете са в страницата (`data-interchange-row`).
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import type { InterchangeGroup } from "../../../../api/inventory";
import { highlightWords } from "../../components/highlight";
import { InterchangeChainChips } from "./InterchangeChainChips";
import { chainLinksOf } from "./interchangeablesShared";

// „Първо се изписва: Кола → Фанта" / „…с най-старата доставка". При два
// продукта всеки има един заместител — приоритетът не се показва. BE връща
// членовете вече подредени (sortIndex, без приоритет — накрая).
const priorityTextOf = (group: InterchangeGroup, t: TFunction): string | null => {
  if (group.members.length < 3) return null;
  const prioritized = group.members.filter((member) => member.sortIndex != null);
  if (!prioritized.length) return t("inventoryPage.ichPriorityOldest");
  const names = prioritized.map((member) => member.productName).join(" → ");
  const text = t("inventoryPage.ichPriorityOrder", { names });
  return prioritized.length < group.members.length
    ? `${text}; ${t("inventoryPage.ichPriorityRest")}`
    : text;
};

type Props = {
  group: InterchangeGroup;
  // думите от търсачката на списъка — оцветяват се в имената и коментара
  searchWords: string[];
  onEdit: () => void;
};

export const InterchangeGroupCard = ({ group, searchWords, onEdit }: Props) => {
  const { t } = useTranslation();
  const comment = group.hint?.trim();
  const priorityText = priorityTextOf(group, t);

  return (
    <div
      data-interchange-row
      role="button"
      tabIndex={0}
      onClick={onEdit}
      onKeyDown={(event) => {
        if (event.key === "Enter" && event.target === event.currentTarget) {
          event.preventDefault();
          onEdit();
        }
      }}
      className="flex cursor-pointer items-center gap-3 border-t border-shade-1 px-[18px] py-3.5 outline-none first:border-t-0 hover:bg-fill focus-visible:shadow-[inset_0_0_0_2px_var(--color-brand)]"
    >
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <InterchangeChainChips links={chainLinksOf(group.members)} searchWords={searchWords} />
        {(priorityText || comment) && (
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1 text-[12.5px] leading-[1.4]">
            {priorityText && <span className="text-muted">{priorityText}</span>}
            {comment && (
              <span className="min-w-0 max-w-full truncate text-body">
                {highlightWords(comment, searchWords)}
              </span>
            )}
          </div>
        )}
      </div>

      <button
        type="button"
        tabIndex={-1}
        onClick={(event) => {
          event.stopPropagation();
          onEdit();
        }}
        className="shrink-0 cursor-pointer whitespace-nowrap rounded-[7px] border border-shade-3 bg-surface px-3 py-1.5 text-[12.5px] font-medium leading-[normal] text-ink outline-none hover:border-shade-5"
      >
        {t("inventoryPage.docEdit")}
      </button>
    </div>
  );
};

export default InterchangeGroupCard;
